import * as React from 'react'

import { IChild } from 'main/gql/child'
import { colors, NativeLink, spacing, Strong, Text, typography } from 'main/ui'
import { useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../common'
import { i18nKeyPrefix } from '../i18n'
import { ChildrenContainer } from './BaseChildrenSelector.styles'
import ChildSection from './ChildSection'
import { IChildEditState } from './ChildSection'

const PRE = `${i18nKeyPrefix}-BASE-CHILDREN-SELECTOR`

interface IProps {
  children: IChild[]
  selectedChildren: IChild[]
  editStates: IChildEditState[]
  multiple: boolean
  validationHint?: string
  onAddChild?: (child: IChild) => void
  onUpdateChild?: (child: IChild) => void
  onChangeSelectedChildren: (child: IChild[]) => void
  onChangeEditStates: (editStates: IChildEditState[]) => void
}

export default function BaseChildrenSelector(props: IProps) {
  const {
    children,
    selectedChildren,
    editStates,
    multiple,
    validationHint,
    onAddChild,
    onUpdateChild,
  } = props

  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const selectorType = multiple ? 'checkbox' : 'radio'
  const addingEditState = editStates.find(editState => editState.childId === null) || null

  return (
    <div>
      <ChildrenContainer>
        {children
          .concat()
          .sort((a, b) => a.id - b.id)
          .map((child, i) => (
            <div key={i}>
              {children.length > 1 && (
                <div css={{ marginBottom: spacing.majorScale(2) }}>
                  <Strong>{t(`${PRE}-COUNT`, { count: i + 1 })}</Strong>
                </div>
              )}

              <ChildSection
                key={child.id}
                child={child}
                editState={editStates.find(editState => editState.childId === child.id) || null}
                selected={!!selectedChildren.find(c => c.id === child.id)}
                selectorType={selectorType}
                onChangeEditState={editState => {
                  handleChangeEditStates(props, { child, editState })
                }}
                onChangeSelected={selected => handleChangeSelectedChild(props, { child, selected })}
                onComplete={onUpdateChild}
              />
            </div>
          ))}

        {addingEditState && (
          <div>
            {!!children.length && (
              <div css={{ marginBottom: spacing.majorScale(2) }}>
                <Strong>{t(`${PRE}-ADD-ANOTHER`)}</Strong>
                <NativeLink
                  css={{ marginLeft: spacing.majorScale(2) }}
                  onClick={() => handleChangeEditStates(props, { child: null, editState: null })}
                >
                  {t(`${PRE}-CANCEL`)}
                </NativeLink>
              </div>
            )}

            <ChildSection
              child={null}
              editState={addingEditState}
              selected={false}
              selectorType={selectorType}
              onChangeEditState={editState =>
                handleChangeEditStates(props, { child: null, editState })
              }
              onComplete={onAddChild}
            />
          </div>
        )}
      </ChildrenContainer>

      {!addingEditState && (
        <div css={{ marginTop: spacing.majorScale(3) }}>
          <NativeLink
            css={{ fontWeight: typography.fontWeight.bold }}
            onClick={() => {
              handleChangeEditStates(props, { child: null, editState: { childId: null } })
            }}
          >
            {t(`${PRE}-ADD`)}
          </NativeLink>
        </div>
      )}

      {validationHint && (
        <div css={{ marginTop: spacing.majorScale(1) }}>
          <Text css={{ color: colors.text.error }}>{validationHint}</Text>
        </div>
      )}
    </div>
  )
}

function handleChangeEditStates(
  props: IProps,
  change: {
    child: IChild | null
    editState: IChildEditState | null
  },
) {
  const { editStates, onChangeEditStates } = props
  const { child, editState } = change

  const childId = child ? child.id : null
  const previous = editStates.find(e => e.childId === childId)

  if (editState && previous) {
    onChangeEditStates(editStates.map(e => (e.childId === childId ? editState : e)))
  } else if (editState && !previous) {
    onChangeEditStates(editStates.concat(editState))
  } else {
    onChangeEditStates(editStates.filter(e => e.childId !== childId))
  }
}

function handleChangeSelectedChild(props: IProps, change: { child: IChild; selected: boolean }) {
  const { selectedChildren, onChangeSelectedChildren } = props
  const { child, selected } = change

  const previouslySelected = !!selectedChildren.find(c => c.id === child.id)

  if (selected && !previouslySelected) {
    onChangeSelectedChildren(selectedChildren.concat(child))
  } else if (!selected && previouslySelected) {
    onChangeSelectedChildren(selectedChildren.filter(c => c.id !== child.id))
  }
}
